import { memo, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { CaseReportPersonsInvolved } from '@/components/casereport/CaseReportPersonsInvolved';
import { CaseReportVehiclesInvolved } from '@/components/casereport/CaseReportVehiclesInvolved';
import { CaseReportBusinessesInvolved } from '@/components/casereport/CaseReportBusinessesInvolved';
import { CaseReportClientsInvolved } from '@/components/casereport/CaseReportClientsInvolved';
import { SearchModal } from '@/components/modals/SearchModal';
import { PageModal } from '@/components/modals/PageModal';
import { NPSDPersonScreen } from '@/components/npsd/NPSDPersonScreen';
import { NPSDVehicleScreen } from '@/components/npsd/NPSDVehicleScreen';
import { NPSDBusinessScreen } from '@/components/npsd/NPSDBusinessScreen';
import { NPSDClientScreenWrapper } from '@/components/npsd/NPSDClientScreen';
import { App } from '@/services/app-compat';
import { CaseRpt } from '@/services/case-report-service';
import { isInArrayByProperty } from '@/utils/arrays';

export interface CaseReportInvolvedSectionHandle {
  isSearchOpen: () => boolean;
  closeSearch: () => void;
  openViewPerson: (id: number) => void;
  openViewVehicle: (id: number) => void;
  openViewBusiness: (id: number) => void;
  openViewClient: (id: number) => void;
}

interface Props {
  people: any[];
  vehicles: any[];
  businesses: any[];
  clients: any[];
  isReadOnly: boolean;
  canSaveNpsdData: boolean;
  onNpsdChange: () => void;
}

export const CaseReportInvolvedSection = memo(forwardRef<CaseReportInvolvedSectionHandle, Props>(
  function CaseReportInvolvedSection({ people, vehicles, businesses, clients, isReadOnly, canSaveNpsdData, onNpsdChange }, ref) {

  // ── Search modal state ────────────────────────────────────────────
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchType, setSearchType] = useState('');

  // ── View entity modal state ───────────────────────────────────────
  const [showViewPersonModal, setShowViewPersonModal] = useState(false);
  const [viewPersonId, setViewPersonId] = useState<number | null>(null);
  const [showViewVehicleModal, setShowViewVehicleModal] = useState(false);
  const [viewVehicleId, setViewVehicleId] = useState<number | null>(null);
  const [showViewBusinessModal, setShowViewBusinessModal] = useState(false);
  const [viewBusinessId, setViewBusinessId] = useState<number | null>(null);
  const [showViewClientModal, setShowViewClientModal] = useState(false);
  const [viewClientId, setViewClientId] = useState<number | null>(null);

  // ── Add new/unknown entity modal state ──────────────────────────────
  const [addEntityModal, setAddEntityModal] = useState<{ type: string; isUnknown: boolean } | null>(null);

  // ── Imperative handle for Vue shim ────────────────────────────────
  useImperativeHandle(ref, () => ({
    isSearchOpen: () => showSearchModal || addEntityModal !== null,
    closeSearch: () => { setShowSearchModal(false); setSearchType(''); setAddEntityModal(null); onNpsdChange(); },
    openViewPerson: (id: number) => { setViewPersonId(id); setShowViewPersonModal(true); },
    openViewVehicle: (id: number) => { setViewVehicleId(id); setShowViewVehicleModal(true); },
    openViewBusiness: (id: number) => { setViewBusinessId(id); setShowViewBusinessModal(true); },
    openViewClient: (id: number) => { setViewClientId(id); setShowViewClientModal(true); },
  }), [showSearchModal, addEntityModal, onNpsdChange]);

  // ── Involved entity handlers ──────────────────────────────────────
  const handleAddExisting = useCallback((npsdType: string) => {
    setSearchType(npsdType);
    setShowSearchModal(true);
  }, []);

  const handleAddNew = useCallback((npsdType: string) => {
    setAddEntityModal({ type: npsdType, isUnknown: false });
  }, []);

  const handleAddUnknown = useCallback((npsdType: string) => {
    setAddEntityModal({ type: npsdType, isUnknown: true });
  }, []);

  const handleView = useCallback((npsdType: string, itemId: number) => {
    if (npsdType === 'Person') { setViewPersonId(itemId); setShowViewPersonModal(true); }
    else if (npsdType === 'Vehicle') { setViewVehicleId(itemId); setShowViewVehicleModal(true); }
    else if (npsdType === 'Business') { setViewBusinessId(itemId); setShowViewBusinessModal(true); }
    else if (npsdType === 'client') { setViewClientId(itemId); setShowViewClientModal(true); }
  }, []);

  const handleRemove = useCallback((npsdType: string, item: any) => {
    const id = item.id;
    const idStr = id.toString();
    const rpt = CaseRpt.currentMDTRpt;

    if (npsdType === 'Person') {
      if (isInArrayByProperty(rpt.digitalEvidenceList, 'involving', idStr + '_P')) {
        App.showPopupNotification('Cannot remove person — used in digital evidence.', 'warning'); return;
      }
      if (isInArrayByProperty(rpt.statementList, 'npsdPerson', id)) {
        App.showPopupNotification('Cannot remove person — used in a statement.', 'warning'); return;
      }
      const idx = rpt.npsdConnectionsNew.people.findIndex((n: any) => n.id.toString() === idStr);
      if (idx > -1) rpt.npsdConnectionsNew.people.splice(idx, 1);
    } else if (npsdType === 'Vehicle') {
      if (isInArrayByProperty(rpt.digitalEvidenceList, 'involving', idStr + '_V')) {
        App.showPopupNotification('Cannot remove vehicle — used in digital evidence.', 'warning'); return;
      }
      const idx = rpt.npsdConnectionsNew.vehicles.findIndex((n: any) => n.id.toString() === idStr);
      if (idx > -1) rpt.npsdConnectionsNew.vehicles.splice(idx, 1);
    } else if (npsdType === 'Business') {
      if (isInArrayByProperty(rpt.digitalEvidenceList, 'involving', idStr + '_B')) {
        App.showPopupNotification('Cannot remove business — used in digital evidence.', 'warning'); return;
      }
      const idx = rpt.npsdConnectionsNew.businesses.findIndex((n: any) => n.id.toString() === idStr);
      if (idx > -1) rpt.npsdConnectionsNew.businesses.splice(idx, 1);
    } else if (npsdType === 'client') {
      if (isInArrayByProperty(rpt.digitalEvidenceList, 'involving', idStr + '_C')) {
        App.showPopupNotification('Cannot remove client — used in digital evidence.', 'warning'); return;
      }
      const idx = rpt.npsdConnectionsNew.clients.findIndex((n: any) => n.id.toString() === idStr);
      if (idx > -1) rpt.npsdConnectionsNew.clients.splice(idx, 1);
    }
    onNpsdChange();
  }, [onNpsdChange]);

  const handlePersonRolesChanged = useCallback((_type: string, personId: number, roles: number[]) => {
    const person = CaseRpt.currentMDTRpt.npsdConnectionsNew.people.find((p: any) => p.id === personId);
    if (person) {
      if (!person.connections) person.connections = { roles: [] };
      person.connections.roles = roles.slice();
      person.roles = roles.slice();
    }
    onNpsdChange();
  }, [onNpsdChange]);

  const handleClientSitesChanged = useCallback((clientId: number, sites: number[]) => {
    const client = CaseRpt.currentMDTRpt.npsdConnectionsNew.clients.find((c: any) => c.id === clientId);
    if (client) client.sites = sites.slice();
    onNpsdChange();
  }, [onNpsdChange]);

  // ── Search modal handlers ─────────────────────────────────────────
  const handleSearchSelect = useCallback((item: any) => {
    const type = searchType.toLowerCase();
    const typeMap: Record<string, string> = {
      person: 'lnkRptAddPerson', vehicle: 'lnkRptAddVehicle',
      business: 'lnkRptAddBusiness', client: 'lnkRptAddClientGroup', clientgroup: 'lnkRptAddClientGroup',
    };
    const linkType = typeMap[type];
    const NPSDItemToReport = (window as any).NPSDItemToReport;
    if (linkType && typeof NPSDItemToReport === 'function') NPSDItemToReport(linkType, item.id);
    setShowSearchModal(false);
    setSearchType('');
    setTimeout(onNpsdChange, 500);
  }, [searchType, onNpsdChange]);

  const handleSearchView = useCallback((item: any) => {
    const type = searchType.toLowerCase();
    if (type === 'person') { setViewPersonId(item.id); setShowViewPersonModal(true); }
    else if (type === 'vehicle') { setViewVehicleId(item.id); setShowViewVehicleModal(true); }
    else if (type === 'business') { setViewBusinessId(item.id); setShowViewBusinessModal(true); }
    else if (type === 'client' || type === 'clientgroup') { setViewClientId(item.id); setShowViewClientModal(true); }
  }, [searchType]);

  const closeSearchModal = useCallback(() => {
    setShowSearchModal(false);
    setSearchType('');
    onNpsdChange();
  }, [onNpsdChange]);


  const closeAddEntityModal = useCallback(() => {
    setAddEntityModal(null);
    onNpsdChange();
  }, [onNpsdChange]);

  return (
    <>
      <div className="tab-panel">
        <CaseReportVehiclesInvolved
          value={vehicles}
          isReadOnly={isReadOnly}
          canSaveNpsdData={canSaveNpsdData}
          onChange={(v: any[]) => { CaseRpt.currentMDTRpt.npsdConnectionsNew.vehicles = v; onNpsdChange(); }}
          onAddExisting={() => handleAddExisting('Vehicle')}
          onAddNew={() => handleAddNew('Vehicle')}
          onAddUnknown={() => handleAddUnknown('Vehicle')}
          onViewItem={(_type: string, id: any) => handleView('Vehicle', id)}
          onRemoveItem={(_type: string, item: any) => handleRemove('Vehicle', item)}
        />
        <CaseReportPersonsInvolved
          persons={people}
          isReadOnly={isReadOnly}
          canSaveNpsdData={canSaveNpsdData}
          onChange={(p: any[]) => { CaseRpt.currentMDTRpt.npsdConnectionsNew.people = p; onNpsdChange(); }}
          onAddExisting={() => handleAddExisting('Person')}
          onAddNew={() => handleAddNew('Person')}
          onAddUnknown={() => handleAddUnknown('Person')}
          onViewItem={(_type: string, id: any) => handleView('Person', id)}
          onRolesChanged={(_type: string, personId: number, roles: number[]) => handlePersonRolesChanged('Person', personId, roles)}
          onRemoveItem={(_type: string, item: any) => handleRemove('Person', item)}
        />
        <CaseReportBusinessesInvolved
          value={businesses}
          isReadOnly={isReadOnly}
          canSaveNpsdData={canSaveNpsdData}
          onChange={(b: any[]) => { CaseRpt.currentMDTRpt.npsdConnectionsNew.businesses = b; onNpsdChange(); }}
          onAddExisting={() => handleAddExisting('Business')}
          onAddNew={() => handleAddNew('Business')}
          onAddUnknown={() => handleAddUnknown('Business')}
          onViewItem={(_type: string, id: any) => handleView('Business', id)}
          onRemoveItem={(_type: string, item: any) => handleRemove('Business', item)}
        />
        <CaseReportClientsInvolved
          value={clients}
          isReadOnly={isReadOnly}
          onChange={(c: any[]) => { CaseRpt.currentMDTRpt.npsdConnectionsNew.clients = c; onNpsdChange(); }}
          onAddExisting={() => handleAddExisting('client')}
          onViewItem={(_type: string, id: any) => handleView('client', id)}
          onRemoveItem={(_type: string, item: any) => handleRemove('client', item)}
          onSitesChanged={handleClientSitesChanged}
        />
      </div>

      {/* Involved Search Modal */}
      {showSearchModal && (
        <SearchModal
          open={showSearchModal}
          searchType={(searchType.toLowerCase() === 'clientgroup' ? 'client' : searchType.toLowerCase()) as 'person' | 'vehicle' | 'business' | 'client'}
          title={`Search ${searchType}`}
          onSelect={handleSearchSelect}
          onView={handleSearchView}
          onCancel={closeSearchModal}
        />
      )}

      {/* View Entity Page Modals */}
      {showViewPersonModal && viewPersonId && (
        <PageModal
          open={showViewPersonModal}
          title="View Person"
          modalId="caseReportViewPerson"
          onClose={() => { setShowViewPersonModal(false); setViewPersonId(null); onNpsdChange(); }}
        >
          <NPSDPersonScreen
            personId={viewPersonId}
            isWindow={true}
            onClose={() => { setShowViewPersonModal(false); setViewPersonId(null); onNpsdChange(); }}
          />
        </PageModal>
      )}
      {showViewVehicleModal && viewVehicleId && (
        <PageModal
          open={showViewVehicleModal}
          title="View Vehicle"
          modalId="caseReportViewVehicle"
          onClose={() => { setShowViewVehicleModal(false); setViewVehicleId(null); onNpsdChange(); }}
        >
          <NPSDVehicleScreen
            vehicleId={viewVehicleId}
            isWindow={true}
            onClose={() => { setShowViewVehicleModal(false); setViewVehicleId(null); onNpsdChange(); }}
          />
        </PageModal>
      )}
      {showViewBusinessModal && viewBusinessId && (
        <PageModal
          open={showViewBusinessModal}
          title="View Business"
          modalId="caseReportViewBusiness"
          onClose={() => { setShowViewBusinessModal(false); setViewBusinessId(null); onNpsdChange(); }}
        >
          <NPSDBusinessScreen
            businessId={viewBusinessId}
            isModal={true}
            onClose={() => { setShowViewBusinessModal(false); setViewBusinessId(null); onNpsdChange(); }}
          />
        </PageModal>
      )}
      {showViewClientModal && viewClientId && (
        <PageModal
          open={showViewClientModal}
          title="View Client"
          modalId="caseReportViewClient"
          onClose={() => { setShowViewClientModal(false); setViewClientId(null); onNpsdChange(); }}
        >
          <NPSDClientScreenWrapper
            clientId={String(viewClientId)}
          />
        </PageModal>
      )}

      {/* Add New / Unknown Entity Modal */}
      {addEntityModal && (
        <PageModal
          open={true}
          title={`${addEntityModal.isUnknown ? 'Unknown' : 'New'} ${addEntityModal.type}`}
          modalId={`caseReportAdd${addEntityModal.type}`}
          onClose={closeAddEntityModal}
        >
          {addEntityModal.type === 'Person' && (
            <NPSDPersonScreen
              isWindow={true}
              isUnknown={addEntityModal.isUnknown}
              startInEditMode={true}
              onClose={closeAddEntityModal}
            />
          )}
          {addEntityModal.type === 'Vehicle' && (
            <NPSDVehicleScreen
              isWindow={true}
              isUnknown={addEntityModal.isUnknown}
              startInEditMode={true}
              onClose={closeAddEntityModal}
            />
          )}
          {addEntityModal.type === 'Business' && (
            <NPSDBusinessScreen
              isModal={true}
              isUnknown={addEntityModal.isUnknown}
              startInEditMode={true}
              onClose={closeAddEntityModal}
            />
          )}
        </PageModal>
      )}
    </>
  );
}));
